/**
 * AppShellLegacy — the legacy sidebar chrome (grouped nav, collapse/hover,
 * framer-motion animations) ported onto the Go-backed /app/* subtree, now
 * carrying the Phase 1 shell contract (docs/design/ux-overhaul-hld.md §0-3,
 * §6; docs/design/phase1-frame-lld.md): a persistent TopBar owns the school
 * switcher, notification bell, and profile menu — none of which are gated
 * by sidebar collapse state anymore — and the sidebar itself is slimmed to
 * brand + nav only.
 *
 * Ported from src/components/layout/Sidebar.tsx + Layout.tsx (read-only
 * sources — do not edit those files, port visuals here instead). Driven
 * entirely by useAuthV2Store; never imports stores/authStore or
 * @varkwave/core. See docs/design/api-migration-map.md Decision U1/U2.
 */
import React, { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Pin, PinOff } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuthV2Store } from '@/features/auth/authV2Store';
import { useOrg } from '@/features/settings/api';
import {
  navigationItems,
  categoryConfig,
  DEFAULT_EXPANDED_CATEGORIES,
  ChevronDown,
  isNavItemActive,
  findActiveCategory,
  findActiveNavItem,
  type NavCategory,
  type ShellNavItem,
} from './navConfig';
import { navItemVisible } from './navAccess';
import { MigratingBadge } from './MigratingBadge';
import { useSelectedSchoolId } from './schoolSelection';
import { TopBar } from './TopBar';

// Group items by category, preserving first-seen category order (matches
// legacy behavior: Object.entries over a reduce-built map).
function groupByCategory(items: ShellNavItem[]): Partial<Record<NavCategory, ShellNavItem[]>> {
  return items.reduce<Partial<Record<NavCategory, ShellNavItem[]>>>((acc, item) => {
    const bucket = acc[item.category] ?? [];
    bucket.push(item);
    acc[item.category] = bucket;
    return acc;
  }, {});
}

interface AppShellLegacyProps {
  children: React.ReactNode;
}

export function AppShellLegacy({ children }: AppShellLegacyProps) {
  const location = useLocation();

  const user = useAuthV2Store((s) => s.user);
  const permissions = useAuthV2Store((s) => s.permissions);
  const { schoolId, selectedSchool } = useSelectedSchoolId();

  // Branding (Slice 4, docs/design/completeness-lld.md): "Enforced = visibly
  // used" — the org logo/name + accent color set in the settings console's
  // BrandingCard render here, so a saved value is never a dead write. Falls
  // back to the default Hardwork mark/title when unset or the image 404s.
  const orgId = user?.org_id;
  const { data: org } = useOrg(orgId);
  const [logoFailed, setLogoFailed] = useState(false);
  const brandColor = org?.brand_color || undefined;
  const logoSrc = !logoFailed && org?.logo_url ? org.logo_url : '/logo.svg';
  const schoolName = selectedSchool?.name?.trim();
  const brandTitle = schoolName || org?.name || 'Hardwork SIS';
  const brandSubtitle =
    schoolName && org?.name && org.name !== schoolName ? org.name : 'School Management System';

  // Nav items the viewer can see: unset `permission` always shows; a set
  // permission hides the item when usePermission(resource, action) would
  // fail. super_admin holds every permission so nothing hides for them.
  // `action` may be a single action or a list of alternatives (any-of) — see
  // NavItemPermission's doc comment.
  const visibleNavigationItems = useMemo(
    () => navigationItems.filter((item) => navItemVisible(item, permissions, schoolId)),
    [permissions, schoolId],
  );
  const groupedNavigation = useMemo(() => groupByCategory(visibleNavigationItems), [visibleNavigationItems]);

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<NavCategory[]>(() => {
    const activeCategory = findActiveCategory(location.pathname);
    if (activeCategory && !DEFAULT_EXPANDED_CATEGORIES.includes(activeCategory)) {
      return [...DEFAULT_EXPANDED_CATEGORIES, activeCategory];
    }
    return DEFAULT_EXPANDED_CATEGORIES;
  });

  const isExpanded = !isCollapsed || isHovering;

  const handleToggleCollapse = () => {
    const newCollapsed = !isCollapsed;
    setIsCollapsed(newCollapsed);
    if (!newCollapsed && !expandedCategories.includes('home')) {
      setExpandedCategories((prev) => [...prev, 'home']);
    }
  };

  const handleMouseEnter = () => {
    if (isCollapsed) {
      setIsHovering(true);
      if (!expandedCategories.includes('home')) {
        setExpandedCategories((prev) => [...prev, 'home']);
      }
    }
  };

  const handleMouseLeave = () => setIsHovering(false);

  const toggleCategory = (category: NavCategory) => {
    setExpandedCategories((prev) =>
      prev.includes(category) ? prev.filter((c) => c !== category) : [...prev, category],
    );
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <div
        className={cn(
          'bg-white shadow-xl border-r border-gray-200 flex flex-col h-full transition-all duration-300 ease-in-out relative z-10',
          isExpanded ? 'w-72' : 'w-20',
        )}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Header */}
        <div className={cn('border-b border-gray-100', isExpanded ? 'p-6' : 'p-4')}>
          <div className={cn('flex items-center', isExpanded ? 'justify-between' : 'flex-col space-y-3')}>
            <div className={cn('flex items-center', isExpanded ? 'space-x-3' : 'flex-col space-y-2')}>
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg overflow-hidden"
                style={brandColor ? { boxShadow: `0 0 0 2px ${brandColor}` } : undefined}
                data-testid="shell-org-logo"
              >
                <img
                  src={logoSrc}
                  alt={brandTitle}
                  className="w-full h-full object-contain"
                  onError={() => setLogoFailed(true)}
                />
              </div>
              {isExpanded && (
                <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }}>
                  <h1
                    className="text-xl font-bold text-gray-900"
                    style={brandColor ? { color: brandColor } : undefined}
                    data-testid="shell-school-name"
                  >
                    {brandTitle}
                  </h1>
                  <p className="text-xs text-gray-500 font-medium" data-testid="shell-org-name">
                    {brandSubtitle}
                  </p>
                </motion.div>
              )}
            </div>
            <button
              onClick={handleToggleCollapse}
              className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
              title={isCollapsed ? 'Pin sidebar' : 'Unpin sidebar'}
            >
              {isCollapsed ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4">
          <div className="px-4 space-y-6">
            {Object.entries(groupedNavigation).map(([category, items]) => {
              if (!items || items.length === 0) return null;
              const categoryInfo = categoryConfig[category as NavCategory];
              const isCategoryExpanded = expandedCategories.includes(category as NavCategory);

              return (
                <div key={category} className="space-y-2">
                  {isExpanded && (
                    <button
                      onClick={() => toggleCategory(category as NavCategory)}
                      className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider hover:text-gray-700 transition-colors"
                    >
                      <span>{categoryInfo.name}</span>
                      <ChevronDown className={cn('w-4 h-4 transition-transform', isCategoryExpanded ? 'rotate-180' : '')} />
                    </button>
                  )}

                  <AnimatePresence initial={false}>
                    {(!isExpanded || isCategoryExpanded) && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className={cn('space-y-1', !isExpanded && 'space-y-2 px-2')}
                      >
                        {items.map((item) => {
                          const isActive = isNavItemActive(item, location.pathname);
                          const Icon = item.icon;

                          if (!item.migrated) {
                            return (
                              <div
                                key={item.name}
                                title={!isExpanded ? item.name : 'moving to the new backend'}
                                className={cn(
                                  'group flex items-center text-sm font-medium relative rounded-xl cursor-not-allowed select-none opacity-60',
                                  !isExpanded ? 'px-0 py-3 justify-center' : 'space-x-3 px-3 py-2.5',
                                )}
                              >
                                <div
                                  className={cn(
                                    'flex items-center justify-center',
                                    !isExpanded ? 'w-10 h-10 rounded-xl' : 'w-5 h-5',
                                    'text-gray-400',
                                  )}
                                >
                                  <Icon className="w-5 h-5" />
                                </div>
                                {isExpanded && (
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between">
                                      <span className="truncate text-gray-500">{item.name}</span>
                                      <MigratingBadge />
                                    </div>
                                    {item.description && (
                                      <p className="text-xs text-gray-400 truncate mt-0.5">{item.description}</p>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          }

                          return (
                            <motion.div key={item.name} whileHover={!isExpanded ? {} : { x: 2 }} whileTap={{ scale: 0.98 }}>
                              <Link
                                to={item.href}
                                className={cn(
                                  'group flex items-center text-sm font-medium transition-all duration-200 relative rounded-xl',
                                  !isExpanded ? 'px-0 py-3 justify-center' : 'space-x-3 px-3 py-2.5',
                                  isActive
                                    ? !isExpanded
                                      ? 'text-white'
                                      : 'bg-blue-50 text-blue-700 border-l-2 border-blue-600'
                                    : 'text-gray-600 hover:text-gray-900',
                                  !isActive && isExpanded && 'hover:bg-gray-50',
                                  !isActive && !isExpanded && 'hover:bg-gray-100',
                                )}
                                title={!isExpanded ? item.name : undefined}
                              >
                                <div
                                  className={cn(
                                    'flex items-center justify-center transition-all duration-200',
                                    !isExpanded
                                      ? isActive
                                        ? 'w-10 h-10 bg-blue-600 rounded-xl shadow-lg'
                                        : 'w-10 h-10 hover:bg-gray-100 rounded-xl'
                                      : 'w-5 h-5',
                                    isActive && isExpanded ? 'text-blue-600' : '',
                                    !isActive && isExpanded ? 'text-gray-400 group-hover:text-gray-600' : '',
                                    isActive && !isExpanded ? 'text-white' : '',
                                    !isActive && !isExpanded ? 'text-gray-400 group-hover:text-gray-600' : '',
                                  )}
                                >
                                  <Icon className="w-5 h-5 transition-all duration-200" />
                                </div>

                                {isExpanded && (
                                  <div className="flex-1 min-w-0">
                                    <span className="truncate block">{item.name}</span>
                                    {item.description && (
                                      <p className="text-xs text-gray-500 truncate mt-0.5">{item.description}</p>
                                    )}
                                  </div>
                                )}
                              </Link>
                            </motion.div>
                          );
                        })}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </nav>
      </div>

      {/* Main content */}
      <main className="flex-1 overflow-y-auto flex flex-col">
        <TopBar onToggleSidebar={handleToggleCollapse} isSidebarCollapsed={isCollapsed} brandColor={brandColor} />
        <div className="flex-1 min-h-0">
          <NavPermissionGate permissions={permissions} schoolId={schoolId} pathname={location.pathname}>
            {children}
          </NavPermissionGate>
        </div>
      </main>
    </div>
  );
}

function NavPermissionGate({
  permissions,
  schoolId,
  pathname,
  children,
}: {
  permissions: string[];
  schoolId?: string;
  pathname: string;
  children: React.ReactNode;
}) {
  const active = findActiveNavItem(pathname);
  if (active && !navItemVisible(active, permissions, schoolId)) {
    return (
      <div className="max-w-lg mx-auto p-12 text-center" data-testid="nav-permission-denied">
        <h2 className="text-lg font-medium text-gray-900 mb-2">You don&apos;t have access to this page</h2>
        <p className="text-sm text-gray-500">
          {active.name} is not available for your role. Use the sidebar to open a section you can access.
        </p>
      </div>
    );
  }
  return <>{children}</>;
}
